import { HTMLProps, ElementType, useId } from "react";
import { Label } from "@radix-ui/react-label";
import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { cn } from "@/utils/helpers";
import { getLang } from "@/utils/selectors";
import Button, { ButtonProps } from "@/components/common/Button";
import Tooltip, { TooltipProps } from "@/components/common/Tooltip";
import styles from "./Toggle.module.css";

export interface ToggleProps extends HTMLProps<HTMLElement> {
	type: string;
	value: ToggleGroup.ToggleGroupItemProps["value"];
	defaultValue?: string;
	options?: string[];
	label?: string;
	tooltipLangParams?: Record<string, any>;
	ButtonProps?: ButtonProps;
	TooltipProps?: TooltipProps;
	IconComponent?: ElementType;
	onToggleChange?: (value: string) => void;
}

export default function Toggle({
	type,
	value,
	defaultValue,
	options,
	label,
	tooltipLangParams,
	ButtonProps,
	TooltipProps,
	IconComponent,
	onToggleChange,
	className
} : ToggleProps) {
	const labelId = useId();
	return (
		<div
			className={cn(
				styles.Toggle,
				className
			)}
		>
			<ToggleGroup.Root
				type="single"
				role="radiogroup"
				value={value}
				defaultValue={defaultValue}
				className={styles.ToggleGroup}
				onValueChange={onToggleChange}
				aria-labelledby={label ? labelId : undefined}
			>
				{label ?
					<Label
						id={labelId}
						className={styles.ToggleLabel}
					>
						{label}
					</Label>
				: null}
				{options?.map(option =>
					<ToggleGroup.Item
						key={option}
						value={option}
						asChild={true}
						className={styles.ToggleButton}
					>
						<Button
							variant="default"
							before={
								IconComponent ?
									<IconComponent
										active={option === value}
										type={option}
									/>
								: undefined
							}
							after={
								<Tooltip
									hasIcon={true}
									inner={getLang(type, option, "tooltip", tooltipLangParams)}
									{...TooltipProps}
								/>
							}
							{...ButtonProps}
						>
							<div
								className={styles.ToggleButtonInner}
								data-text={getLang(type, option)}
							>
								{getLang(type, option)}
							</div>
						</Button>
					</ToggleGroup.Item>
				)}
			</ToggleGroup.Root>
		</div>
	)
};